import moment from 'moment';
import { formatBuddhistEra } from 'src/app/shared/pipes/thai-datetime.pipe';
import { bahttext } from 'bahttext';
import { toNumber as _toNumber, toString as _toString } from 'lodash';
import {
    SellerEntity,
    toSellerPrintBlock,
} from 'src/app/shared/config/entity-config';
import { splitVatInclusive } from 'src/app/shared/config/vat';
import { InvoiceParameter } from '../print-sheet/interfaces/invoice-param';
import { TaxInvoicePrintSheetParameter } from '../print-sheet/interfaces/tax-invoice-param';
import {
    OrderItem,
    OutputTax,
} from '../output-tax/interfaces/output-tax-request';

// Pure builders for tax invoices generated from marketplace orders
// (CONSOLE-BUILD-SPEC W14). One document shape only: the FULL tax invoice
// (ม.86/4) carrying the buyer's confirmed details — a collector request or
// a Tier-1 confirmed recognizer receipt. The console never issues
// ใบกำกับภาษีอย่างย่อ (decided 2026-09-02; the ม.86/6 branch that existed
// until then was removed outright): an order with no confirmed buyer keeps
// the plain receipt sheet and gets no output-tax row.
// The document prints as a set (delivery note / tax invoice / receipt on one
// sheet) and is VAT-INCLUSIVE: the order's net total is what the buyer paid,
// and base + VAT are back-solved from it at the tax-point date.

export interface TaxInvoiceBuyer {
    name: string;
    address: string;
    taxId: string;
    branch: string;
}

const FULL_TITLES = {
    title1: 'ใบส่งสินค้า / ใบกำกับภาษี / ใบเสร็จรับเงิน',
    title2: '(Delivery Note / Tax Invoice / Receipt)',
};

const thaiDate = (m: moment.Moment): string =>
    formatBuddhistEra(m, 'DD MMMM YYYY');

const thaiDateAbb = (m: moment.Moment): string =>
    formatBuddhistEra(m, 'DD/MM/YYYY');

// The output-tax save payload for one marketplace order. The order's identity
// travels with the row — (platform, storeId, orderSn) is the idempotency key
// the fetch-or-generate lookup and the Lambda's partial unique index both
// use. orderSn stays a STRING: 19-digit ids exceed JS safe integers.
export const toOutputTaxRow = (
    order: {
        storeId: string;
        platform: 'lazada' | 'shopee' | 'tiktok';
        orderId: string;
        createDate: Date;
    },
    invParam: InvoiceParameter,
    buyer: TaxInvoiceBuyer,
    entity: SellerEntity,
): OutputTax => {
    const seller = toSellerPrintBlock(entity);
    // The tax point is order creation (spec §4.5) — it decides the month the
    // sale falls into and the rate it is split at.
    const taxPoint = moment(order.createDate);
    const dateIso = taxPoint.format('YYYY-MM-DD');
    const gross = invParam.orderInfo.netPrice;
    const split = splitVatInclusive(gross, dateIso);
    return {
        fileSharedLink: null,
        entityId: entity.id,
        docType: 'full',
        storeId: order.storeId,
        platform: order.platform,
        orderSn: _toString(order.orderId),
        'sellerInfo.name': seller.name,
        'sellerInfo.branch': seller.branch,
        'sellerInfo.taxId': seller.taxId,
        'sellerInfo.website': seller.website,
        'sellerInfo.address': seller.address,
        'sellerInfo.email': seller.email,
        'customerInfo.name': buyer.name,
        'customerInfo.branch': buyer.branch,
        'customerInfo.address': buyer.address,
        'customerInfo.taxId': buyer.taxId,
        'orderInfo.invNum': null, // minted by the Lambda per entity series
        'orderInfo.orderNo': _toString(order.orderId),
        'orderInfo.date': _toNumber(taxPoint.format('YYYYMMDD')),
        'orderInfo.dateD': _toNumber(taxPoint.format('D')),
        'orderInfo.dateM': _toNumber(taxPoint.format('M')),
        'orderInfo.dateY': _toNumber(taxPoint.format('YYYY')),
        'orderInfo.totalProductPrice': invParam.orderInfo.total || 0,
        'orderInfo.discount': invParam.orderInfo.discount || 0,
        'orderInfo.shippingCost': invParam.orderInfo.shippingCost || 0,
        'orderInfo.amtBeforeVat': split.base,
        'orderInfo.totalVat': split.output,
        'orderInfo.netPrice': gross,
        'orderInfo.remark': _toString(invParam.orderInfo.remark ?? '').trim(),
        items: invParam.items.map((item): OrderItem => ({
            // Marketplace lines carry no product id; the line number is
            // the per-invoice key the item store needs (order_products is
            // keyed {invNum, pid}).
            pid: item.itemNo,
            sku: '',
            itemNo: item.itemNo,
            description: _toString(item.description).trim(),
            unitPrice: item.unitPrice,
            quantity: item.quantity,
            amount: item.amount,
        })),
    };
};

const docInfoFor = (
    isCopy: boolean,
): TaxInvoicePrintSheetParameter['docInfo'] => ({
    // Matches the manual form's defaults: signature boxes shown, seller
    // signature image not stamped.
    hideSignatureFields: false,
    hideSellerSignature: true,
    title1: FULL_TITLES.title1,
    title2: FULL_TITLES.title2,
    title3: isCopy ? 'สำเนา (เอกสารออกเป็นชุด)' : 'ต้นฉบับ (เอกสารออกเป็นชุด)',
    priceIncludesVat: true,
});

// The print page for a row built THIS run (generation path): everything is
// still in hand, so the page derives from the same save payload the Lambda
// just persisted, plus the number it minted.
export const toTaxInvoicePage = (
    row: OutputTax,
    invNum: string,
    matchingNumber: string,
): TaxInvoicePrintSheetParameter => {
    const taxPoint = moment(_toString(row['orderInfo.date']), 'YYYYMMDD');
    return {
        sellerInfo: {
            name: row['sellerInfo.name'],
            branch: row['sellerInfo.branch'],
            taxId: row['sellerInfo.taxId'],
            website: row['sellerInfo.website'],
            address: row['sellerInfo.address'],
            email: row['sellerInfo.email'],
        },
        customerInfo: {
            name: row['customerInfo.name'],
            branch: row['customerInfo.branch'],
            address: row['customerInfo.address'],
            taxId: row['customerInfo.taxId'],
        },
        orderInfo: {
            invNum,
            orderNo: _toString(row['orderInfo.orderNo']),
            matchingNumber,
            date: thaiDate(taxPoint),
            tableTotal:
                (row['orderInfo.totalProductPrice'] || 0) +
                (row['orderInfo.shippingCost'] || 0),
            discount: row['orderInfo.discount'] || 0,
            shippingCost: row['orderInfo.shippingCost'] || 0,
            totalExclVat: row['orderInfo.amtBeforeVat'] || 0,
            vatAmt: row['orderInfo.totalVat'] || 0,
            netPrice: row['orderInfo.netPrice'] || 0,
            netPriceInWords: bahttext(row['orderInfo.netPrice'] || 0),
            sellerSignDate: thaiDateAbb(taxPoint),
            remark: row['orderInfo.remark'] || '',
        },
        docInfo: docInfoFor(false),
        items: row.items.map((item) => ({
            itemNo: item.itemNo,
            description: item.description,
            unitPrice: item.unitPrice,
            quantity: item.quantity,
            amount: item.amount,
        })),
    };
};

// A row with no stored docInfo doesn't say which way its VAT ran, and the
// two modes share every relationship except one: the item table sums to the
// NET on a VAT-inclusive row (VAT was backed out of what the buyer paid)
// but to the BASE on an add-on row (VAT was added on top of it). Marketplace
// rows are always the former; manual rows saved before docInfo persistence
// (2026-08-10) are typically the latter — assuming "inclusive" printed their
// สำเนา with a รวมในราคาแล้ว tag over add-on figures (found 2026-08-28 on
// the 3 Aug documents). Rows too old to carry the table fields keep the old
// assumption.
const inferPriceIncludesVat = (orderInfo: any): boolean => {
    const tableAfterDiscount =
        (orderInfo?.totalProductPrice || 0) +
        (orderInfo?.shippingCost || 0) -
        (orderInfo?.discount || 0);
    if (tableAfterDiscount <= 0) {
        return true;
    }
    const addedOnTop =
        Math.abs((orderInfo?.amtBeforeVat || 0) - tableAfterDiscount) < 0.01 &&
        (orderInfo?.netPrice || 0) > tableAfterDiscount + 0.01;
    return !addedOnTop;
};

// Manual-form rows persist the exact doc shape the ต้นฉบับ printed
// (docInfo.title1/2, the document-set marker, signature toggles) — prefer
// it, deriving only the ต้นฉบับ/สำเนา corner mark. Marketplace rows carry
// no stored docInfo and keep the full document-set titles.
const docInfoFromRow = (
    row: any,
    isCopy: boolean,
): TaxInvoicePrintSheetParameter['docInfo'] => {
    const stored = row?.docInfo;
    if (stored?.title1 === undefined) {
        return {
            ...docInfoFor(isCopy),
            priceIncludesVat: inferPriceIncludesVat(row?.orderInfo),
        };
    }
    const mark = isCopy ? 'สำเนา' : 'ต้นฉบับ';
    return {
        hideSignatureFields: stored.hideSignatureFields === true,
        hideSellerSignature: stored.hideSellerSignature === true,
        title1: _toString(stored.title1),
        title2: _toString(stored.title2 ?? ''),
        title3: stored.docSet === true ? `${mark} (เอกสารออกเป็นชุด)` : mark,
        priceIncludesVat: stored.priceIncludesVat === true,
    };
};

// The print page for a row FETCHED from the store (reprint at pack time, and
// the month-end สำเนา batch). The seller comes from the row's own stored
// block — never the live registry: the row records what was printed, and a
// later registry edit must not rewrite an issued document.
export const toTaxInvoicePageFromRow = (
    row: any,
    options: { isCopy?: boolean; matchingNumber?: string } = {},
): TaxInvoicePrintSheetParameter => {
    const orderInfo = row?.orderInfo ?? {};
    const taxPoint = moment(_toString(orderInfo.date), 'YYYYMMDD');
    const netPrice = orderInfo.netPrice || 0;
    return {
        sellerInfo: {
            name: _toString(row?.sellerInfo?.name ?? ''),
            branch: _toString(row?.sellerInfo?.branch ?? ''),
            taxId: _toString(row?.sellerInfo?.taxId ?? ''),
            website: _toString(row?.sellerInfo?.website ?? ''),
            address: _toString(row?.sellerInfo?.address ?? ''),
            email: _toString(row?.sellerInfo?.email ?? ''),
        },
        customerInfo: {
            name: _toString(row?.customerInfo?.name ?? ''),
            branch: _toString(row?.customerInfo?.branch ?? ''),
            address: _toString(row?.customerInfo?.address ?? ''),
            taxId: _toString(row?.customerInfo?.taxId ?? ''),
        },
        orderInfo: {
            invNum: _toString(orderInfo.invNum ?? ''),
            orderNo: _toString(orderInfo.orderNo ?? ''),
            ...(options.matchingNumber
                ? { matchingNumber: options.matchingNumber }
                : {}),
            date: taxPoint.isValid() ? thaiDate(taxPoint) : '',
            tableTotal:
                (orderInfo.totalProductPrice || 0) +
                (orderInfo.shippingCost || 0),
            discount: orderInfo.discount || 0,
            shippingCost: orderInfo.shippingCost || 0,
            totalExclVat: orderInfo.amtBeforeVat || 0,
            vatAmt: orderInfo.totalVat || 0,
            netPrice,
            netPriceInWords: bahttext(netPrice),
            sellerSignDate: taxPoint.isValid() ? thaiDateAbb(taxPoint) : '',
            remark: _toString(orderInfo.remark ?? ''),
        },
        docInfo: docInfoFromRow(row, options.isCopy === true),
        items: (row?.items ?? []).map((item: any) => ({
            itemNo: item.itemNo,
            description: _toString(item.description ?? ''),
            unitPrice: item.unitPrice,
            quantity: item.quantity,
            amount: item.amount,
        })),
    };
};
